// Server-wide organization foundation — one cached source of the department tree
// + people, shared by every module (approval, mail, projects, calendar, …).
// Backed by /iam/departments/tree and /users?include_dept=1.
import { useEffect, useState } from "react";
import { api } from "../api/client";

export type DeptNode = { id: number; name: string; children: DeptNode[] };
export type OrgUser = {
  id: number; name: string; username?: string | null; email?: string | null;
  department_id?: number | null; department_name?: string | null;
  position_name?: string | null; status?: string;
};
export type OrgData = { tree: DeptNode[]; users: OrgUser[] };

let cache: OrgData | null = null;
let inflight: Promise<OrgData> | null = null;

/** Load (and cache) the whole org. Concurrent callers share one request. */
export async function loadOrg(force = false): Promise<OrgData> {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;
  inflight = Promise.all([
    api.get<DeptNode[]>("/iam/departments/tree"),
    api.get<{ data: OrgUser[] }>("/users?size=500&include_dept=1"),
  ])
    .then(([t, u]) => {
      cache = { tree: t.data || [], users: (u.data?.data || []).filter((x) => x.status !== "INACTIVE") };
      inflight = null;
      return cache;
    })
    .catch((e) => { inflight = null; throw e; });
  return inflight;
}

/** Drop the cache (call after org edits in the directory admin). */
export function invalidateOrg(): void { cache = null; }

/** Members of a department (by department_id). */
export function membersOf(users: OrgUser[], deptId: number): OrgUser[] {
  return users.filter((u) => u.department_id === deptId);
}

/** React hook — returns the cached org, fetching once if needed. */
export function useOrg() {
  const [data, setData] = useState<OrgData | null>(cache);
  const [loading, setLoading] = useState(!cache);
  useEffect(() => {
    if (cache) { setData(cache); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    loadOrg()
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return {
    tree: data?.tree ?? [],
    users: data?.users ?? [],
    loading,
    reload: () => loadOrg(true).then((d) => setData(d)).catch(() => {}),
  };
}
